import { Product } from "../../types";
import { DeliveryArea } from "../../services/deliveryAreaService";
import { FaqEntry, FaqTarget, FaqTopic } from "../../services/answerDeskService";
import { getEffectivePrice } from "../../utils/productPricing";
import { STOREFRONT_URL } from "../../config/storefront";

// ── Topics ───────────────────────────────────────────────────────────────────

export const TOPICS: { value: FaqTopic; label: string }[] = [
    { value: "PRODUCT_INFO", label: "Product info" },
    { value: "USAGE", label: "Usage & feeding" },
    { value: "HEALTH", label: "Health & safety" },
    { value: "DELIVERY", label: "Delivery" },
    { value: "PAYMENT", label: "Payment" },
    { value: "RETURNS_ORDERS", label: "Returns & orders" },
    { value: "AUTHENTICITY", label: "Authenticity" },
    { value: "STORE_INFO", label: "Store info" },
    { value: "QUICK_REPLY", label: "Quick replies" },
    { value: "OTHER", label: "Other" },
];

export const topicLabel = (topic: FaqTopic): string =>
    TOPICS.find((t) => t.value === topic)?.label ?? "Other";

// ── Storefront link (same slug rule as cps-ecom getProductSlugParam) ────────

const slugify = (text: string): string =>
    text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");

/** Null when the product isn't on the storefront (hidden, inactive, merged). */
export function storefrontLink(product: Product): string | null {
    if (!product.is_active || !product.is_visible || product.is_merged) return null;
    return `${STOREFRONT_URL}/products/${slugify(product.name)}-${product.product_id}`;
}

// ── Live product facts ───────────────────────────────────────────────────────

/** "৳1,250" — chat-friendly, no decimals unless there are paisa. */
export function taka(amount: number): string {
    const n = Number(amount) || 0;
    const hasFraction = Math.round(n * 100) % 100 !== 0;
    return `৳${n.toLocaleString("en-IN", {
        minimumFractionDigits: hasFraction ? 2 : 0,
        maximumFractionDigits: hasFraction ? 2 : 0,
    })}`;
}

/** Sellable units right now — quantity minus what's reserved by orders. */
export function availableStock(product: Product): number | null {
    if (!product.stock) return null;
    return Math.max(0, (product.stock.quantity || 0) - (product.stock.reserved_quantity || 0));
}

// Same "only N left" threshold the storefront uses (cps-ecom LOW_STOCK_DISPLAY_MAX).
const LOW_STOCK_MAX = 5;

export function stockWords(product: Product): string {
    const available = availableStock(product);
    if (available === null || available <= 0) return "out of stock";
    if (available <= LOW_STOCK_MAX) return `only ${available} left`;
    return "in stock";
}

export function stockTone(product: Product): "success" | "warning" | "error" {
    const available = availableStock(product);
    if (available === null || available <= 0) return "error";
    if (available <= LOW_STOCK_MAX) return "warning";
    return "success";
}

export function weightText(product: Product): string {
    const kg = Number(product.weight);
    if (!kg || !Number.isFinite(kg)) return "";
    return kg < 1 ? `${Math.round(kg * 1000)} g` : `${Number(kg.toFixed(3))} kg`;
}

export function priceLine(product: Product): string {
    const effective = getEffectivePrice(product);
    if (product.is_on_sale && Number(product.selling_price) > effective) {
        return `${taka(effective)} (was ${taka(product.selling_price)})`;
    }
    return taka(effective);
}

/** What "Copy product card" puts on the clipboard. */
export function productCardText(product: Product): string {
    const stock = stockWords(product);
    const lines = [product.name, `${priceLine(product)} · ${stock.charAt(0).toUpperCase()}${stock.slice(1)}`];
    const link = storefrontLink(product);
    if (link) lines.push(link);
    return lines.join("\n");
}

export function productListText(products: Product[]): string {
    return products
        .map((p, i) => {
            const link = storefrontLink(p);
            return `${i + 1}) ${p.name} — ${priceLine(p)} · ${stockWords(p)}${link ? `\n${link}` : ""}`;
        })
        .join("\n");
}

/** Active delivery areas, grouped when they share a charge and threshold. */
export function deliveryChargesText(areas: DeliveryArea[]): string {
    const active = areas
        .filter((a) => a.is_active)
        .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0) || a.name.localeCompare(b.name));
    const groups: { names: string[]; charge: number; free: number | null }[] = [];
    for (const area of active) {
        const charge = Number(area.delivery_charge);
        const free = area.free_above_amount == null ? null : Number(area.free_above_amount);
        const last = groups[groups.length - 1];
        if (last && last.charge === charge && last.free === free) last.names.push(area.name);
        else groups.push({ names: [area.name], charge, free });
    }
    return groups
        .map((g) => `• ${g.names.join(", ")} — ${taka(g.charge)}${g.free !== null ? ` (free over ${taka(g.free)})` : ""}`)
        .join("\n");
}

// ── Placeholders ─────────────────────────────────────────────────────────────

export const PRODUCT_PLACEHOLDERS = [
    "name",
    "price",
    "regular_price",
    "discount",
    "stock",
    "link",
    "brand",
    "category",
    "weight",
] as const;

export const ALL_PLACEHOLDERS: { key: string; label: string }[] = [
    { key: "name", label: "name" },
    { key: "price", label: "price" },
    { key: "regular_price", label: "regular price" },
    { key: "discount", label: "discount" },
    { key: "stock", label: "stock" },
    { key: "link", label: "link" },
    { key: "brand", label: "brand" },
    { key: "category", label: "category" },
    { key: "weight", label: "weight" },
    { key: "delivery_charges", label: "delivery charges" },
];

const PLACEHOLDER_RE = /\{(name|price|regular_price|discount|stock|link|brand|category|weight|delivery_charges)\}/g;
const PRODUCT_PLACEHOLDER_RE = /\{(name|price|regular_price|discount|stock|link|brand|category|weight)\}/;

export const needsProduct = (text: string | null | undefined): boolean =>
    !!text && PRODUCT_PLACEHOLDER_RE.test(text);

export interface Segment {
    text: string;
    /** Filled from live data (highlighted in the UI). */
    live: boolean;
    /** A placeholder that couldn't be filled (no product in the chat). */
    missing: boolean;
}

function productValue(key: string, product: Product): string {
    switch (key) {
        case "name":
            return product.name;
        case "price":
            return taka(getEffectivePrice(product));
        case "regular_price":
            return taka(product.selling_price);
        case "discount":
            return product.is_on_sale && product.discount_percent ? `${product.discount_percent}% off` : "";
        case "stock":
            return stockWords(product);
        case "link":
            return storefrontLink(product) ?? "";
        case "brand":
            return product.brand?.name ?? "";
        case "category":
            return product.category?.name ?? "";
        case "weight":
            return weightText(product);
        default:
            return "";
    }
}

/** Split an answer into plain and live-filled pieces. */
export function fillAnswer(
    text: string,
    product: Product | null,
    deliveryAreas: DeliveryArea[],
): Segment[] {
    const segments: Segment[] = [];
    let last = 0;
    PLACEHOLDER_RE.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = PLACEHOLDER_RE.exec(text)) !== null) {
        if (m.index > last) segments.push({ text: text.slice(last, m.index), live: false, missing: false });
        const key = m[1];
        if (key === "delivery_charges") {
            const value = deliveryChargesText(deliveryAreas);
            segments.push(value ? { text: value, live: true, missing: false } : { text: m[0], live: false, missing: true });
        } else if (product) {
            segments.push({ text: productValue(key, product), live: true, missing: false });
        } else {
            segments.push({ text: m[0], live: false, missing: true });
        }
        last = m.index + m[0].length;
    }
    if (last < text.length) segments.push({ text: text.slice(last), live: false, missing: false });
    return segments;
}

export const segmentsToText = (segments: Segment[]): string => segments.map((s) => s.text).join("");

// ── Scope labels ─────────────────────────────────────────────────────────────

export function targetLabel(t: FaqTarget): string {
    if (t.product_id) return t.product_name ?? "Deleted product";
    const parts: string[] = [];
    if (t.brand_id) parts.push(t.brand_name ?? "Deleted brand");
    if (t.category_id) {
        parts.push(`${t.category_name ?? "Deleted category"}${t.include_subcategories ? "" : " (only)"}`);
    }
    return parts.join(" · ");
}

export type ScopeKind = "product" | "brand_category" | "category" | "brand" | "general" | "none";

export function scopeOf(entry: FaqEntry): { kind: ScopeKind; label: string } {
    if (entry.is_general) return { kind: "general", label: "General" };
    const t = entry.targets;
    if (t.length === 0) return { kind: "none", label: "Needs a product or category" };
    const products = t.filter((x) => x.product_id);
    if (products.length === t.length) {
        return products.length === 1
            ? { kind: "product", label: `Product: ${products[0].product_name ?? "deleted"}` }
            : { kind: "product", label: `${products.length} products` };
    }
    if (t.length === 1) {
        const only = t[0];
        if (only.brand_id && only.category_id) return { kind: "brand_category", label: targetLabel(only) };
        if (only.category_id) return { kind: "category", label: `Category: ${targetLabel(only)}` };
        if (only.brand_id) return { kind: "brand", label: `Brand: ${targetLabel(only)}` };
    }
    return { kind: "category", label: `${t.length} rules` };
}

// ── Search ───────────────────────────────────────────────────────────────────

const BANGLA_DIGITS = "০১২৩৪৫৬৭৮৯";

export function normalize(text: string): string {
    return text
        .normalize("NFC")
        .toLowerCase()
        .replace(/[০-৯]/g, (d) => String(BANGLA_DIGITS.indexOf(d)))
        .replace(/[^\p{L}\p{M}\p{N}]+/gu, " ")
        .trim();
}

interface IndexedField {
    text: string;
    weight: number;
}

export interface SearchIndexItem {
    entry: FaqEntry;
    fields: IndexedField[];
}

export function buildSearchIndex(entries: FaqEntry[]): SearchIndexItem[] {
    return entries.map((entry) => ({
        entry,
        fields: [
            { text: normalize(entry.question), weight: 4 },
            ...entry.alt_phrasings.map((p) => ({ text: normalize(p), weight: 4 })),
            ...entry.targets.map((t) => ({ text: normalize(targetLabel(t)), weight: 2 })),
            { text: normalize(topicLabel(entry.topic)), weight: 1.5 },
            { text: normalize(entry.answer ?? ""), weight: 1 },
        ].filter((f) => f.text),
    }));
}

export interface SearchHit {
    entry: FaqEntry;
    score: number;
    partial: boolean;
}

/**
 * Every word must appear somewhere (question and phrasings count most).
 * If nothing matches all words, fall back to entries matching at least
 * half of them, marked partial.
 */
export function searchEntries(index: SearchIndexItem[], query: string): SearchHit[] {
    const tokens = Array.from(new Set(normalize(query).split(" ").filter(Boolean)));
    if (tokens.length === 0) return [];

    const scored = index.map(({ entry, fields }) => {
        let score = 0;
        let found = 0;
        for (const token of tokens) {
            let best = 0;
            for (const f of fields) {
                if (!f.text.includes(token)) continue;
                const wordStart = f.text.startsWith(token) || f.text.includes(` ${token}`);
                best = Math.max(best, f.weight * (wordStart ? 1.5 : 1));
            }
            if (best > 0) {
                found += 1;
                score += best;
            }
        }
        return { entry, score, found };
    });

    const full = scored.filter((s) => s.found === tokens.length);
    if (full.length > 0) return full.map((s) => ({ entry: s.entry, score: s.score, partial: false }));
    const need = Math.ceil(tokens.length / 2);
    return scored
        .filter((s) => s.found >= need && s.found > 0)
        .map((s) => ({ entry: s.entry, score: s.score, partial: true }));
}

// ── Misc ─────────────────────────────────────────────────────────────────────

export function timeAgo(iso: string | null | undefined): string {
    if (!iso) return "never";
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
    if (days <= 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 30) return `${days} days ago`;
    const months = Math.floor(days / 30);
    return months === 1 ? "a month ago" : `${months} months ago`;
}

export const STALE_DAYS = 90;

export function isStale(entry: FaqEntry): boolean {
    if (entry.status !== "PUBLISHED") return false;
    if (!entry.last_reviewed_at) return true;
    return Date.now() - new Date(entry.last_reviewed_at).getTime() > STALE_DAYS * 86_400_000;
}

/** The API's own words when it has them (FastAPI `detail`), else a fallback. */
export function apiErrorMessage(err: unknown, fallback: string): string {
    const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length > 0) {
        const first = detail[0] as { msg?: string };
        if (first?.msg) return first.msg.replace(/^Value error, /, "");
    }
    return fallback;
}

/** Clipboard with a fallback for non-secure contexts. */
export async function copyText(text: string): Promise<boolean> {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        /* fall through */
    }
    // Selecting the hidden textarea takes focus; give it back afterwards so
    // the cashier can keep typing (e.g. in the quick-answer search box).
    const previousFocus = document.activeElement as HTMLElement | null;
    try {
        const area = document.createElement("textarea");
        area.value = text;
        area.setAttribute("readonly", "");
        area.style.position = "fixed";
        area.style.opacity = "0";
        document.body.appendChild(area);
        area.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(area);
        return ok;
    } catch {
        return false;
    } finally {
        previousFocus?.focus?.();
    }
}

// ── Keyboard: copy the highlighted answer ───────────────────────────────────

const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);

/** For hints: "⌘C" on a Mac, "Ctrl+C" elsewhere. */
export const COPY_SHORTCUT = IS_MAC ? "⌘C" : "Ctrl+C";

/**
 * Should this ⌘C / Ctrl+C copy the highlighted answer (like Enter)? Not when
 * you've selected some text yourself — part of the search, or a bit of an
 * answer — or you're in another field or a menu: then it's a normal copy.
 * The whole search selected is how the quick-answer box opens, so that still
 * copies the answer. `code` covers Bangla and other keyboard layouts.
 */
export function copiesHighlightedAnswer(e: KeyboardEvent, searchInput: HTMLInputElement | null): boolean {
    if (e.altKey || e.shiftKey || e.isComposing) return false;
    if (!(IS_MAC ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey)) return false;
    if (e.key?.toLowerCase() !== "c" && e.code !== "KeyC") return false;
    const target = e.target as HTMLElement | null;
    if (searchInput && target === searchInput) {
        const { selectionStart: start, selectionEnd: end, value } = searchInput;
        return start === end || (start === 0 && end === value.length);
    }
    if (inAnotherField(target)) return false;
    return !window.getSelection()?.toString();
}

/** A field, menu or list of its own (not the answers search) — keys there are its own. */
const inAnotherField = (target: HTMLElement | null): boolean =>
    !!target?.closest("input, textarea, [contenteditable='true'], [role='menu'], [role='listbox']");

/** For hints: the first image's shortcut — "⌥C" on a Mac, "Alt+C" elsewhere. */
export const IMAGE_COPY_SHORTCUT = IS_MAC ? "⌥C" : "Alt+C";

/** For hints: image n's shortcut — "⌘⌥2" on a Mac, "Ctrl+Alt+2" elsewhere. */
export const imageNumberShortcut = (n: number): string => (IS_MAC ? `⌘⌥${n}` : `Ctrl+Alt+${n}`);

/**
 * Which of the highlighted answer's images this key press copies: ⌥C
 * (Alt+C) → the first, ⌘⌥1–9 (Ctrl+Alt+1–9) → that one; null for any other
 * key, or while typing in another field. Matched by physical key (`code`),
 * since ⌥ changes the character typed on a Mac and on Bangla/other layouts.
 */
export function imageShortcutIndex(e: KeyboardEvent, searchInput: HTMLInputElement | null): number | null {
    if (!e.altKey || e.shiftKey || e.isComposing) return null;
    const primary = IS_MAC ? e.metaKey : e.ctrlKey;
    if (IS_MAC ? e.ctrlKey : e.metaKey) return null;
    const target = e.target as HTMLElement | null;
    if (target !== searchInput && inAnotherField(target)) return null;
    if (!primary && e.code === "KeyC") return 0;
    const digit = /^(?:Digit|Numpad)([1-9])$/.exec(e.code);
    return primary && digit ? Number(digit[1]) - 1 : null;
}

/** Why the image shortcut can't copy image `index` of this answer, or null if it can. */
export function imageShortcutProblem(entry: FaqEntry, index: number): string | null {
    const count = entry.images?.length ?? 0;
    if (count === 0) return "This answer has no images.";
    if (index >= count) return `This answer has only ${count} image${count === 1 ? "" : "s"}.`;
    return null;
}

/**
 * Why Enter / ⌘C can't copy this answer straight away — it fills in a
 * product and there isn't exactly one to fill in — or null when it can.
 * Said out loud, because a silent no-op would leave the old clipboard to be
 * pasted to the customer.
 */
export function productChoiceNeeded(entry: FaqEntry, candidates: Product[]): string | null {
    if (!needsProduct(entry.answer) || candidates.length === 1) return null;
    return candidates.length === 0
        ? "Nothing copied — this answer fills in a product. Add the product first."
        : "Nothing copied — this answer differs by product. Use its Copy button to pick one.";
}
